import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SQUAD_CONSTANTS } from '@app/modules/squad/constants/squad.constants';
import { ISquadAbsenceChange, ISquadPlayer, SquadAbsenceReason } from '@app/modules/squad/interfaces/squad.interface';
import { absenceOn, toDay } from '@app/modules/squad/utils/squad-absence.utils';

/**
 * Marks one player as out for longer (reason + optional last day) or available again. Every change
 * is emitted right away; the parent saves it and passes the updated player back in.
 * Used by the squad page and the attendance picker.
 */
@Component({
    selector: 'app-absence-panel',
    templateUrl: './absence-panel.component.html',
    styleUrl: './absence-panel.component.scss',
    standalone: false,
})
export class AbsencePanelComponent implements OnChanges {
    @Input({ required: true }) player!: ISquadPlayer;
    /** Day (`YYYY-MM-DD`) the absence is checked against; also the earliest selectable end date. */
    @Input() day: string = toDay(new Date());
    /** Set by the parent when saving failed. */
    @Input() error = false;
    @Output() absenceChange = new EventEmitter<ISquadAbsenceChange>();
    @Output() closed = new EventEmitter<void>();

    readonly reasons = SQUAD_CONSTANTS.ABSENCE_REASONS;
    readonly reasonIcons = SQUAD_CONSTANTS.ABSENCE_ICONS;

    /** End date in the date field; kept while the same player is edited. */
    draftUntil = '';

    ngOnChanges(changes: SimpleChanges): void {
        const previous = changes['player']?.previousValue as ISquadPlayer | undefined;
        if (changes['player'] && previous?.uuid !== this.player.uuid) {
            this.draftUntil = (this.reason && this.player.absentUntil) || '';
        }
    }

    /** Current reason on `day`; an absence that already ended counts as none. */
    get reason(): SquadAbsenceReason | null {
        return absenceOn(this.player, this.day);
    }

    get name(): string {
        return `${this.player.firstName} ${this.player.lastName}`;
    }

    setReason(reason: SquadAbsenceReason): void {
        this.absenceChange.emit({ player: this.player, reason, until: this.draftUntil || null });
    }

    /** A new end date is saved right away once the player already has a reason. */
    setUntil(until: string): void {
        this.draftUntil = until;
        if (this.reason) {
            this.absenceChange.emit({ player: this.player, reason: this.reason, until: until || null });
        }
    }

    clear(): void {
        this.absenceChange.emit({ player: this.player, reason: null, until: null });
        this.closed.emit();
    }
}
